import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, ArrowUpDown, ArrowDown, ArrowUp, X, Sparkles, TrendingUp,
  DollarSign, BarChart3, MessageSquare, Layers, Globe, ChevronDown, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { authFetch } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => ({ 'Content-Type': 'application/json' });

/* ─── Helpers ─── */
const kdColor = (kd) => {
  const v = parseInt(kd) || 0;
  if (v <= 20) return { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Easy' };
  if (v <= 40) return { bg: 'bg-green-100', text: 'text-green-700', label: 'Moderate' };
  if (v <= 60) return { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Hard' };
  if (v <= 80) return { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Very Hard' };
  return { bg: 'bg-red-100', text: 'text-red-700', label: 'Extreme' };
};
const fmtVol = (v) => { const n = parseInt(v) || 0; return n >= 1e6 ? `${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `${(n/1e3).toFixed(1)}K` : n.toString(); };
const fmtCpc = (v) => `$${(parseFloat(v) || 0).toFixed(2)}`;
const fmtComp = (v) => `${((parseFloat(v) || 0) * 100).toFixed(0)}%`;

const VolumeBar = ({ volume, maxVolume }) => {
  const pct = maxVolume > 0 ? Math.min((volume / maxVolume) * 100, 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium w-14 text-right">{fmtVol(volume)}</span>
      <div className="flex-1 h-2 bg-surface-page rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-brand to-brand-hover rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

/* ─── Cluster Colors ─── */
const CLUSTER_COLORS = [
  { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-600' },
  { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', badge: 'bg-violet-600' },
  { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-600' },
  { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-600' },
  { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', badge: 'bg-rose-600' },
  { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', badge: 'bg-cyan-600' },
  { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', badge: 'bg-orange-600' },
  { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', badge: 'bg-teal-600' },
];

/* ─── AI Clustering ─── */
function clusterKeywords(semrushKws, kwpuData) {
  const clusters = {};
  const addTo = (name, kw) => {
    if (!clusters[name]) clusters[name] = [];
    if (!clusters[name].find(k => k.toLowerCase() === kw.toLowerCase())) clusters[name].push(kw);
  };

  // Cluster SEMRush keywords by pattern
  for (const kw of semrushKws) {
    const word = (kw.keyword || '').toLowerCase();
    if (!word) continue;

    if (/\b(what|how|why|when|where|which|can|does|is|are)\b/.test(word))
      addTo('Questions & Research', kw.keyword);
    else if (/\b(vs|versus|alternative|compared|comparison|better)\b/.test(word))
      addTo('Comparison & Alternatives', kw.keyword);
    else if (/\b(best|top|review|rated|recommended)\b/.test(word))
      addTo('Reviews & Best-of', kw.keyword);
    else if (/\b(cost|price|pricing|cheap|affordable|free|fee)\b/.test(word))
      addTo('Pricing & Cost', kw.keyword);
    else if (/\b(near me|in \w+|local|\bcity\b|\bstate\b)\b/.test(word))
      addTo('Local / Geo-Modified', kw.keyword);
    else if (/\b(tool|software|platform|app|solution)\b/.test(word))
      addTo('Tools & Software', kw.keyword);
    else if (/\b(agency|company|service|firm|provider|consultant)\b/.test(word))
      addTo('Services & Agencies', kw.keyword);
    else
      addTo('Core Keywords', kw.keyword);
  }

  // Add KWPU autocomplete as "Long-Tail / Autocomplete"
  if (kwpuData?.autocomplete?.length) {
    for (const ac of kwpuData.autocomplete) {
      addTo('Long-Tail / Autocomplete', ac);
    }
  }

  // Add KWPU semantic as enrichment
  if (kwpuData?.semantic?.length) {
    for (const sem of kwpuData.semantic.slice(0, 30)) {
      addTo('Semantic / Related', sem);
    }
  }

  // Add forum questions
  if (kwpuData?.forums?.reddit?.length) {
    for (const q of kwpuData.forums.reddit) addTo('Reddit Questions', q);
  }
  if (kwpuData?.forums?.quora?.length) {
    for (const q of kwpuData.forums.quora) addTo('Quora Questions', q);
  }

  // Convert to array sorted by size
  return Object.entries(clusters)
    .map(([name, keywords], i) => ({ name, keywords, color: CLUSTER_COLORS[i % CLUSTER_COLORS.length] }))
    .sort((a, b) => b.keywords.length - a.keywords.length);
}

/* ═══════════════════════ MAIN COMPONENT ═══════════════════════ */
const KeywordResearch = ({ user }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [database, setDatabase] = useState('us');
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('table'); // table | clusters | insights
  const [semrushData, setSemrushData] = useState(null);
  const [kwpuData, setKwpuData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [sortField, setSortField] = useState('search_volume');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedClusters, setExpandedClusters] = useState(new Set());

  /* ─── Search ─── */
  const search = useCallback(async () => {
    const seeds = query.split(',').map(s => s.trim()).filter(Boolean);
    if (!seeds.length) { toast.error('Enter at least one keyword'); return; }
    setLoading(true);
    setSelected(new Set());
    try {
      const res = await authFetch(`${API}/api/keywords/research`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ keywords: seeds, database }),
      });
      if (!res.ok) throw new Error('API error');
      const data = await res.json();
      setSemrushData(data.semrush || null);
      setKwpuData(data.kwpu || null);
    } catch (e) {
      toast.error('Failed to fetch keyword data');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, database, API]);

  /* ─── Sorting ─── */
  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={12} className="text-text-muted" />;
    return sortDir === 'asc' ? <ArrowUp size={12} className="text-brand" /> : <ArrowDown size={12} className="text-brand" />;
  };

  const sortedKeywords = useMemo(() =>
    (semrushData?.all_unique_keywords || []).slice().sort((a, b) => {
      const aVal = sortField === 'keyword' ? (a.keyword || '') : (parseFloat(a[sortField]) || 0);
      const bVal = sortField === 'keyword' ? (b.keyword || '') : (parseFloat(b[sortField]) || 0);
      if (sortField === 'keyword') return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    }), [semrushData, sortField, sortDir]
  );

  const maxVolume = useMemo(() => sortedKeywords.reduce((m, kw) => Math.max(m, parseInt(kw.search_volume) || 0), 0), [sortedKeywords]);

  /* ─── Clusters ─── */
  const clusters = useMemo(() => clusterKeywords(sortedKeywords, kwpuData), [sortedKeywords, kwpuData]);

  /* ─── Selection ─── */
  const toggleSelect = (kw) => setSelected(prev => { const n = new Set(prev); n.has(kw) ? n.delete(kw) : n.add(kw); return n; });
  const selectAll = () => selected.size === sortedKeywords.length ? setSelected(new Set()) : setSelected(new Set(sortedKeywords.map(k => k.keyword)));
  const selectCluster = (cluster) => {
    setSelected(prev => {
      const n = new Set(prev);
      const allSelected = cluster.keywords.every(k => n.has(k));
      cluster.keywords.forEach(k => allSelected ? n.delete(k) : n.add(k));
      return n;
    });
  };

  const toggleClusterExpand = (name) => setExpandedClusters(prev => {
    const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n;
  });

  /* ─── Create Brief ─── */
  const createBriefFromSelection = () => {
    const selectedKws = sortedKeywords.filter(k => selected.has(k.keyword));
    const kwStrings = [...selected];
    sessionStorage.setItem('prefill_keywords', JSON.stringify(kwStrings));
    sessionStorage.setItem('prefill_keyword_data', JSON.stringify(selectedKws));
    navigate('/internal/workflows/briefs/create');
    toast.success(`${kwStrings.length} keywords ready for brief creation`);
  };

  /* ─── Stats ─── */
  const hasData = semrushData || kwpuData;
  const statsCards = semrushData ? [
    { icon: BarChart3, label: 'SEMRush Keywords', value: semrushData.total_keywords || 0, color: 'text-brand' },
    { icon: TrendingUp, label: 'Avg Volume', value: fmtVol(sortedKeywords.reduce((s, k) => s + (parseInt(k.search_volume) || 0), 0) / (sortedKeywords.length || 1)), color: 'text-brand' },
    { icon: DollarSign, label: 'Avg CPC', value: fmtCpc(sortedKeywords.reduce((s, k) => s + (parseFloat(k.cpc) || 0), 0) / (sortedKeywords.length || 1)), color: 'text-bcm-orange' },
    { icon: Layers, label: 'Clusters', value: clusters.length, color: 'text-brand' },
  ] : null;

  const kwpuStats = kwpuData ? [
    { label: 'Autocomplete', value: kwpuData.autocomplete?.length || 0 },
    { label: 'Semantic', value: kwpuData.semantic?.length || 0 },
    { label: 'Reddit', value: kwpuData.forums?.reddit?.length || 0 },
    { label: 'Quora', value: kwpuData.forums?.quora?.length || 0 },
  ] : null;

  const TABS = [
    { id: 'table', label: 'SEMRush Data', icon: BarChart3 },
    { id: 'clusters', label: 'Keyword Clusters', icon: Layers },
    { id: 'insights', label: 'KWPU Insights', icon: MessageSquare },
  ];

  return (
    <div className="space-y-6 animate-fade-up" data-testid="keyword-research">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-semibold tracking-tight text-text-primary mb-1">Keyword Research</h2>
        <p className="text-text-secondary">SEMRush metrics + KWPU intent data + AI clustering</p>
      </div>

      {/* Search */}
      <Card className="glass">
        <CardContent className="p-4">
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="text-xs font-medium text-text-secondary mb-1 block">Seed Keywords (comma-separated)</label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
                <Input placeholder="e.g. ppc management, google ads agency" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && search()} className="pl-9" data-testid="keyword-search-input" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-text-secondary mb-1 block">Market</label>
              <select value={database} onChange={e => setDatabase(e.target.value)} className="h-10 px-3 border border-input rounded-xl bg-white text-sm" data-testid="market-select">
                <option value="us">US</option><option value="uk">UK</option><option value="ca">Canada</option><option value="au">Australia</option><option value="de">Germany</option><option value="fr">France</option>
              </select>
            </div>
            <Button onClick={search} disabled={loading} className="bg-brand hover:bg-brand-hover text-white gap-2 h-10" data-testid="keyword-search-btn">
              {loading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : <Search size={16} />}
              Research
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats Row */}
      {statsCards && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {statsCards.map((s, i) => (
            <Card key={`item-${i}`} className="glass">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl bg-white flex items-center justify-center ${s.color}`}><s.icon size={20} /></div>
                <div><p className="text-xs text-text-tertiary">{s.label}</p><p className="text-lg font-bold text-text-primary">{s.value}</p></div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* KWPU mini-stats */}
      {kwpuStats && (
        <div className="flex gap-3 items-center px-4 py-2.5 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg" data-testid="kwpu-stats-badge">
          <span className="px-2.5 py-1 bg-amber-600 text-white text-xs font-bold rounded-full uppercase tracking-wide">KWPU</span>
          <span className="text-sm text-amber-800">
            {kwpuStats.map(s => `${s.value} ${s.label}`).join(' · ')}
          </span>
        </div>
      )}

      {/* Selection Bar */}
      {selected.size > 0 && (
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-brand text-white rounded-xl shadow-lg" data-testid="selection-bar">
          <span className="font-medium">{selected.size} keyword{selected.size > 1 ? 's' : ''} selected</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="border-white/30 text-white hover:bg-white/20 gap-1" onClick={() => setSelected(new Set())}><X size={14} /> Clear</Button>
            <Button size="sm" className="bg-white text-brand hover:bg-white/90 gap-1 font-semibold" onClick={createBriefFromSelection} data-testid="create-brief-from-keywords"><Sparkles size={14} /> Create Brief</Button>
          </div>
        </div>
      )}

      {/* Tabs */}
      {hasData && (
        <div className="flex gap-1 bg-white/50 rounded-xl p-1 border border-border-subtle" data-testid="research-tabs">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === t.id ? 'bg-brand text-white shadow' : 'text-text-secondary hover:bg-surface-page'}`} data-testid={`tab-${t.id}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>
      )}

      {/* ═══ TAB: SEMRush Table ═══ */}
      {tab === 'table' && sortedKeywords.length > 0 && (
        <Card className="glass overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="keyword-results-table">
              <thead>
                <tr className="border-b border-border-subtle bg-white/50">
                  <th className="px-4 py-3 text-left w-10">
                    <input type="checkbox" checked={selected.size === sortedKeywords.length && sortedKeywords.length > 0} onChange={selectAll} className="rounded border-border-strong" data-testid="select-all-checkbox" />
                  </th>
                  {[
                    { key: 'keyword', label: 'Keyword', align: 'text-left' },
                    { key: 'search_volume', label: 'Volume', align: 'text-left' },
                    { key: 'cpc', label: 'CPC', align: 'text-right' },
                    { key: 'competition', label: 'Competition', align: 'text-right' },
                    { key: 'keyword_difficulty', label: 'Difficulty (KD)', align: 'text-center' },
                  ].map(col => (
                    <th key={col.key} className={`px-4 py-3 ${col.align} text-xs font-semibold text-text-secondary uppercase tracking-wider cursor-pointer hover:text-brand select-none`} onClick={() => toggleSort(col.key)}>
                      <span className="inline-flex items-center gap-1">{col.label} <SortIcon field={col.key} /></span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedKeywords.map((kw, i) => {
                  const kd = kdColor(kw.keyword_difficulty);
                  const isSel = selected.has(kw.keyword);
                  return (
                    <tr key={kw.keyword || `kw-${i}`} className={`border-b border-border-subtle hover:bg-brand/5 transition-colors cursor-pointer ${isSel ? 'bg-brand/10' : ''}`} onClick={() => toggleSelect(kw.keyword)} data-testid={`keyword-row-${i}`}>
                      <td className="px-4 py-2.5"><input type="checkbox" checked={isSel} onChange={() => toggleSelect(kw.keyword)} onClick={e => e.stopPropagation()} className="rounded border-border-strong" /></td>
                      <td className="px-4 py-2.5"><span className="text-sm font-medium text-text-primary">{kw.keyword}</span></td>
                      <td className="px-4 py-2.5 min-w-[180px]"><VolumeBar volume={parseInt(kw.search_volume) || 0} maxVolume={maxVolume} /></td>
                      <td className="px-4 py-2.5 text-right"><span className="text-sm font-medium text-text-primary">{fmtCpc(kw.cpc)}</span></td>
                      <td className="px-4 py-2.5 text-right"><span className="text-sm text-text-secondary">{fmtComp(kw.competition)}</span></td>
                      <td className="px-4 py-2.5 text-center"><span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${kd.bg} ${kd.text}`}>{kw.keyword_difficulty} <span className="opacity-70">· {kd.label}</span></span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-white/30 border-t border-border-subtle flex items-center justify-between text-xs text-text-tertiary">
            <span>{sortedKeywords.length} keywords · SEMRush</span>
            <span>Seed: {semrushData?.seed_overview?.length || 0} · Related: {semrushData?.related_keywords?.length || 0} · Phrase: {semrushData?.phrase_match_keywords?.length || 0}</span>
          </div>
        </Card>
      )}

      {/* ═══ TAB: Clusters ═══ */}
      {tab === 'clusters' && clusters.length > 0 && (
        <div className="space-y-3" data-testid="clusters-view">
          <div className="flex items-center justify-between">
            <p className="text-sm text-text-secondary">{clusters.length} keyword clusters identified from SEMRush + KWPU data</p>
            <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={() => setExpandedClusters(prev => prev.size === clusters.length ? new Set() : new Set(clusters.map(c => c.name)))}>
              {expandedClusters.size === clusters.length ? 'Collapse All' : 'Expand All'}
            </Button>
          </div>

          {clusters.map((cluster, ci) => {
            const isExpanded = expandedClusters.has(cluster.name);
            const clusterSelected = cluster.keywords.filter(k => selected.has(k)).length;
            const allSelected = clusterSelected === cluster.keywords.length;

            return (
              <Card key={`cluster-${cluster.name}`} className={`overflow-hidden border ${cluster.color.border} ${cluster.color.bg}`} data-testid={`cluster-${ci}`}>
                <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => toggleClusterExpand(cluster.name)}>
                  {isExpanded ? <ChevronDown size={16} className={cluster.color.text} /> : <ChevronRight size={16} className={cluster.color.text} />}
                  <span className={`px-2.5 py-1 ${cluster.color.badge} text-white text-xs font-bold rounded-full`}>{cluster.keywords.length}</span>
                  <span className={`font-semibold text-sm ${cluster.color.text}`}>{cluster.name}</span>
                  <div className="flex-1" />
                  <Button
                    size="sm"
                    variant={allSelected ? 'default' : 'outline'}
                    className={`text-xs h-7 ${allSelected ? 'bg-brand text-white' : ''}`}
                    onClick={(e) => { e.stopPropagation(); selectCluster(cluster); }}
                    data-testid={`select-cluster-${ci}`}
                  >
                    {allSelected ? 'Deselect All' : clusterSelected > 0 ? `${clusterSelected} selected` : 'Select All'}
                  </Button>
                </div>

                {isExpanded && (
                  <div className="px-4 pb-3">
                    <div className="flex flex-wrap gap-1.5">
                      {cluster.keywords.map((kw, ki) => {
                        const isSel = selected.has(kw);
                        const srData = sortedKeywords.find(s => s.keyword?.toLowerCase() === kw.toLowerCase());
                        return (
                          <button
                            key={kw}
                            onClick={() => toggleSelect(kw)}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs transition-all border ${isSel ? 'bg-brand text-white border-brand' : 'bg-white border-border-subtle text-text-primary hover:border-brand/40'}`}
                          >
                            {kw}
                            {srData && (
                              <span className={`${isSel ? 'text-white/70' : 'text-text-tertiary'}`}>
                                {fmtVol(srData.search_volume)}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* ═══ TAB: KWPU Insights ═══ */}
      {tab === 'insights' && kwpuData && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="kwpu-insights">
          {/* Autocomplete */}
          <Card className="glass">
            <CardHeader className="pb-2">
              <CardTitle className="text-brand text-base flex items-center gap-2"><Globe size={18} /> Google Autocomplete</CardTitle>
            </CardHeader>
            <CardContent>
              {kwpuData.autocomplete?.length > 0 ? (
                <div className="space-y-1 max-h-80 overflow-y-auto">
                  {kwpuData.autocomplete.map((kw, i) => (
                    <button key={kw || `kw-${i}`} onClick={() => toggleSelect(kw)} className={`w-full text-left px-3 py-1.5 rounded-lg text-sm transition-colors ${selected.has(kw) ? 'bg-brand text-white' : 'hover:bg-brand/5 text-text-primary'}`}>
                      {kw}
                    </button>
                  ))}
                </div>
              ) : <p className="text-sm text-text-tertiary">No autocomplete data</p>}
            </CardContent>
          </Card>

          {/* Semantic */}
          <Card className="glass">
            <CardHeader className="pb-2">
              <CardTitle className="text-brand text-base flex items-center gap-2"><Layers size={18} /> Semantic Keywords</CardTitle>
            </CardHeader>
            <CardContent>
              {kwpuData.semantic?.length > 0 ? (
                <div className="flex flex-wrap gap-1.5 max-h-80 overflow-y-auto">
                  {kwpuData.semantic.map((kw, i) => (
                    <button key={`neg-${kw}`} onClick={() => toggleSelect(kw)} className={`px-2.5 py-1 rounded-full text-xs transition-all border ${selected.has(kw) ? 'bg-brand text-white border-brand' : 'bg-white border-border-subtle text-text-primary hover:border-brand/40'}`}>
                      {kw}
                    </button>
                  ))}
                </div>
              ) : <p className="text-sm text-text-tertiary">No semantic data</p>}
            </CardContent>
          </Card>

          {/* Reddit */}
          <Card className="glass">
            <CardHeader className="pb-2">
              <CardTitle className="text-bcm-orange text-base flex items-center gap-2"><MessageSquare size={18} /> Reddit Questions</CardTitle>
            </CardHeader>
            <CardContent>
              {kwpuData.forums?.reddit?.length > 0 ? (
                <div className="space-y-1.5 max-h-80 overflow-y-auto">
                  {kwpuData.forums.reddit.map((q, i) => (
                    <div key={`item-${i}`} className="px-3 py-2 bg-orange-50 border border-orange-100 rounded-lg text-sm text-text-primary">{q}</div>
                  ))}
                </div>
              ) : <p className="text-sm text-text-tertiary">No Reddit questions</p>}
            </CardContent>
          </Card>

          {/* Quora */}
          <Card className="glass">
            <CardHeader className="pb-2">
              <CardTitle className="text-red-600 text-base flex items-center gap-2"><MessageSquare size={18} /> Quora Questions</CardTitle>
            </CardHeader>
            <CardContent>
              {kwpuData.forums?.quora?.length > 0 ? (
                <div className="space-y-1.5 max-h-80 overflow-y-auto">
                  {kwpuData.forums.quora.map((q, i) => (
                    <div key={`item-${i}`} className="px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-sm text-text-primary">{q}</div>
                  ))}
                </div>
              ) : <p className="text-sm text-text-tertiary">No Quora questions</p>}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Empty State */}
      {!hasData && !loading && (
        <Card className="glass">
          <CardContent className="p-12 text-center">
            <Search size={48} className="mx-auto text-brand/20 mb-4" />
            <h3 className="text-lg font-semibold text-text-primary mb-2">Research Keywords with SEMRush + KWPU</h3>
            <p className="text-text-tertiary text-sm max-w-md mx-auto">
              Enter seed keywords to get real search volume, CPC, difficulty (SEMRush) plus autocomplete suggestions, semantic keywords, and forum questions (KWPU). Keywords are auto-clustered into themed ad groups.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {loading && (
        <Card className="glass">
          <CardContent className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-text-primary mb-2">Researching Keywords...</h3>
            <p className="text-text-tertiary text-sm">Querying SEMRush + KeywordsPeopleUse in parallel</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default KeywordResearch;
